import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";
import clsx from "clsx";
import { QuantityDiffRow } from "../QuantityDiffRow";
import { STATUS_DOT_CLASS } from "../../constants";
import { SECTION_HEADER_STICKY_CLASS } from "../constants";
import type { QuantityDiffStatus } from "../../utils/computeQuantityDiff";
import type { QuantityDiffSectionProps } from "./types";

interface SectionHeadingProps {
  title: string;
  status: QuantityDiffStatus;
  entryCount: number;
}

const SectionHeading = ({ title, status, entryCount }: SectionHeadingProps) => (
  <>
    <span className={clsx("h-2 w-2 rounded-full", STATUS_DOT_CLASS[status])} />
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
      {title}
    </span>
    <span className="text-xs text-gray-500">{entryCount}</span>
  </>
);

export const QuantityDiffSection = ({
  title,
  status,
  entries,
  kind,
  isCollapsible = false,
}: QuantityDiffSectionProps) => {
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded] = useState(!isCollapsible);

  if (entries.length === 0) return null;

  return (
    <section className="mb-4">
      {isCollapsible ? (
        <button
          type="button"
          onClick={() => setIsExpanded((previous) => !previous)}
          className={clsx(
            SECTION_HEADER_STICKY_CLASS,
            "flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-gray-50",
          )}
        >
          <SectionHeading
            title={title}
            status={status}
            entryCount={entries.length}
          />
          <span className="ml-auto flex items-center gap-1 text-xs text-gray-500">
            {isExpanded
              ? t("smartSurveyor.planComparison.quantities.hide")
              : t("smartSurveyor.planComparison.quantities.show")}
            {isExpanded ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )}
          </span>
        </button>
      ) : (
        <div
          className={clsx(
            SECTION_HEADER_STICKY_CLASS,
            "flex items-center gap-2 px-3 py-2",
          )}
        >
          <SectionHeading
            title={title}
            status={status}
            entryCount={entries.length}
          />
        </div>
      )}
      {isExpanded && (
        <div>
          {entries.map((entry) => (
            <QuantityDiffRow key={entry.key} entry={entry} kind={kind} />
          ))}
        </div>
      )}
    </section>
  );
};
